import { z } from 'zod';
import { decimalNoNegativo, decimalPositivo, paginacion, uuid } from './comun.ts';

export const UNIDADES = ['uni', 'kg', 'mts', 'm2', 'caja', 'saco', 'juego', 'galon', 'lt', 'par', 'rollo', 'pza'] as const;
export type Unidad = (typeof UNIDADES)[number];

/** Producto con inventario o servicio sin inventario (corte de lámina, flete…). */
export const TIPOS_PRODUCTO = ['producto', 'servicio'] as const;
export type TipoProducto = (typeof TIPOS_PRODUCTO)[number];

/**
 * Fracción mínima de venta: 1 = solo unidades enteras · 0,5 = medios (lámina de vidrio) · 0,25 = cuartos ·
 * 0,1 y 0,01 para lo que se vende por peso o por metro.
 */
export const FRACCIONES_MINIMAS = ['1', '0.5', '0.25', '0.1', '0.01'] as const;
export const FRACCION_ETIQUETA: Record<(typeof FRACCIONES_MINIMAS)[number], string> = {
  '1': 'Solo enteros',
  '0.5': 'Medios (0,5)',
  '0.25': 'Cuartos (0,25)',
  '0.1': 'Décimas (0,1)',
  '0.01': 'Centésimas (0,01): peso o metros',
};
const fraccionMinima = z.enum(FRACCIONES_MINIMAS);

const laminaMm = z.number().int().min(1).max(10000);

export const productoCrearInput = z.object({
  codigo: z.string().trim().min(1, 'El código es obligatorio').max(40).toUpperCase(),
  nombre: z.string().trim().min(2, 'Mínimo 2 caracteres').max(160),
  descripcion: z.string().trim().max(2000).default(''),
  categoria_id: uuid.nullable().default(null),
  subcategoria_id: uuid.nullable().default(null),
  color: z.string().trim().max(60).nullable().default(null),
  marca: z.string().trim().max(80).nullable().default(null),
  unidad: z.enum(UNIDADES),
  fragil: z.boolean().default(false),
  despiece_producto_id: uuid.nullable().default(null),
  despiece_factor: decimalPositivo.nullable().default(null),
  /** Servicio = renglón sin inventario (no reserva, no se entrega, no entra a conteos). */
  tipo: z.enum(TIPOS_PRODUCTO).default('producto'),
  /** Fracción mínima de venta, en unidades base. Por defecto: enteros (o centésimas para kg, mts, m2 y lt). */
  fraccion_minima: fraccionMinima.optional(),
  /** Lámina de vidrio: largo × ancho en milímetros y espesor. Con la medida se revisan las piezas de una rotura (spec 06). */
  lamina_largo_mm: laminaMm.nullable().default(null),
  lamina_ancho_mm: laminaMm.nullable().default(null),
  lamina_espesor_mm: decimalPositivo.nullable().default(null),
});
export type ProductoCrearInput = z.infer<typeof productoCrearInput>;
/** Valores del formulario ANTES de aplicar defaults (react-hook-form + zodResolver). */
export type ProductoCrearEntrada = z.input<typeof productoCrearInput>;

/**
 * Edición PARCIAL: sin `.default()` — un `.partial()` sobre el schema de creación seguiría
 * aplicando los defaults y borraría campos no enviados (bug reproducido en test/bugs/productos-02).
 */
export const productoEditarInput = z.object({
  codigo: z.string().trim().min(1).max(40).toUpperCase().optional(),
  nombre: z.string().trim().min(2).max(160).optional(),
  descripcion: z.string().trim().max(2000).optional(),
  categoria_id: uuid.nullable().optional(),
  subcategoria_id: uuid.nullable().optional(),
  color: z.string().trim().max(60).nullable().optional(),
  marca: z.string().trim().max(80).nullable().optional(),
  unidad: z.enum(UNIDADES).optional(),
  fragil: z.boolean().optional(),
  despiece_producto_id: uuid.nullable().optional(),
  despiece_factor: decimalPositivo.nullable().optional(),
  tipo: z.enum(TIPOS_PRODUCTO).optional(),
  fraccion_minima: fraccionMinima.optional(),
  lamina_largo_mm: laminaMm.nullable().optional(),
  lamina_ancho_mm: laminaMm.nullable().optional(),
  lamina_espesor_mm: decimalPositivo.nullable().optional(),
  activo: z.boolean().optional(),
});
export type ProductoEditarInput = z.infer<typeof productoEditarInput>;

/** Orden acotado al rango integer de Postgres (evita 500 por overflow). */
const orden = z.number().int().min(0).max(1_000_000);

export const presentacionInput = z.object({
  nombre: z.string().trim().min(1).max(60),
  unidades: decimalPositivo,
  orden: orden.default(0),
});
export type PresentacionInput = z.infer<typeof presentacionInput>;

export const presentacionEditarInput = z.object({
  nombre: z.string().trim().min(1).max(60).optional(),
  orden: orden.optional(),
  activa: z.boolean().optional(),
});
export type PresentacionEditarInput = z.infer<typeof presentacionEditarInput>;

/** Matriz de precios: varias celdas (presentación × lista) en una sola mutación auditada. */
export const preciosInput = z.object({
  precios: z
    .array(z.object({ presentacion_id: uuid, lista_id: uuid, precio_unitario_divisa: decimalNoNegativo }))
    .min(1, 'Indica al menos un precio')
    .max(200),
});
export type PreciosInput = z.infer<typeof preciosInput>;

export const minimoInput = z.object({ sucursal_id: uuid, minimo: decimalNoNegativo });
/** Mínimo de existencia en un depósito o tienda concretos (alerta por depósito). */
export const minimoAlmacenInput = z.object({ almacen_id: uuid, minimo: decimalNoNegativo });
export type MinimoAlmacenInput = z.infer<typeof minimoAlmacenInput>;
export type MinimoInput = z.infer<typeof minimoInput>;

export const listaPrecioInput = z.object({
  codigo: z.string().trim().min(2).max(30).toLowerCase().regex(/^[a-z0-9_-]+$/, 'Solo minúsculas, números, guiones'),
  nombre: z.string().trim().min(2).max(60),
  orden: orden.default(0),
});
export type ListaPrecioInput = z.infer<typeof listaPrecioInput>;

export const listaPrecioEditarInput = z.object({
  nombre: z.string().trim().min(2).max(60).optional(),
  orden: orden.optional(),
  activa: z.boolean().optional(),
});
export type ListaPrecioEditarInput = z.infer<typeof listaPrecioEditarInput>;

export const categoriaInput = z.object({
  nombre: z.string().trim().min(2).max(60),
  padre_id: uuid.nullable().default(null),
});
export type CategoriaInput = z.infer<typeof categoriaInput>;

/** Renombrar o activar/desactivar una categoría (nunca se borra). */
export const categoriaEditarInput = z.object({ nombre: z.string().trim().min(2).max(60).optional(), activa: z.boolean().optional() });
export type CategoriaEditarInput = z.infer<typeof categoriaEditarInput>;

export const productosFiltros = paginacion.extend({
  q: z.string().trim().max(120).optional(),
  categoria_id: uuid.optional(),
  activo: z
    .enum(['true', 'false', 'todos'])
    .default('true')
    .transform((v) => (v === 'todos' ? undefined : v === 'true')),
  /** Lista para la cual devolver precios (default: la lista default). */
  lista_id: uuid.optional(),
  /** Solo cajas que se despiezan: frágiles con su pieza suelta y piezas por caja (despiece de cajas). */
  despiezables: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  /** Solo productos con existencia < mínimo en mi sucursal. */
  bajo_minimo: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});
export type ProductosFiltros = z.infer<typeof productosFiltros>;

// ─── DTOs ───────────────────────────────────────────────────────────────────
export interface ListaPrecioDTO {
  id: string;
  codigo: string;
  nombre: string;
  orden: number;
  es_default: boolean;
  activa: boolean;
}

export interface CategoriaDTO {
  id: string;
  nombre: string;
  padre_id: string | null;
  activa: boolean;
}

export interface PrecioDTO {
  presentacion_id: string;
  lista_id: string;
  precio_unitario_divisa: string;
}

export interface PresentacionDTO {
  id: string;
  producto_id: string;
  nombre: string;
  unidades: string;
  orden: number;
  activa: boolean;
  /** precios por lista (solo las listas activas) */
  precios: PrecioDTO[];
}

/** Existencia frente a su mínimo: bajo · cerca (lo supera por menos del porcentaje configurado) · ok · sin mínimo. */
export type EstadoMinimo = 'bajo' | 'cerca' | 'ok' | 'sin_minimo';

export interface StockAlmacenDTO {
  almacen_id: string;
  almacen_nombre: string;
  almacen_tipo: 'tienda' | 'deposito';
  principal: boolean;
  existencia: string;
  pendiente: string;
  disponible: string;
  /** Mínimo fijado para este almacén (null = sin mínimo). */
  minimo: string | null;
  estado_minimo: EstadoMinimo;
}

export interface ProductoResumenDTO {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string;
  categoria: string | null;
  subcategoria: string | null;
  color: string | null;
  marca: string | null;
  unidad: string;
  fragil: boolean;
  /** 'servicio' = sin inventario. */
  tipo: TipoProducto;
  /** Fracción mínima de venta en unidades base ("1.00", "0.50"…). */
  fraccion_minima: string;
  /** Medida de la lámina de vidrio (mm) y su espesor; null si no es una lámina. */
  lamina_largo_mm: number | null;
  lamina_ancho_mm: number | null;
  lamina_espesor_mm: string | null;
  activo: boolean;
  /** Disponible total en los almacenes de MI sucursal (null para roles globales sin contexto). */
  disponible_sucursal: string | null;
  existencia_sucursal: string | null;
  minimo_sucursal: string | null;
  bajo_minimo: boolean;
  /** Disponible por almacén de MI sucursal (tienda y depósitos), en el orden de los almacenes; null sin contexto. Pedido del cliente 03-10-2026. */
  stock_por_almacen: { almacen_id: string; almacen_nombre: string; tipo: 'tienda' | 'deposito'; disponible: string; existencia: string; minimo: string | null; estado_minimo: EstadoMinimo }[] | null;
  /** Precio unitario ($ divisa) de la presentación de 1 unidad en la lista pedida. */
  precio_unitario_divisa: string | null;
  presentaciones: PresentacionDTO[];
}

export interface ProductoDetalleDTO extends ProductoResumenDTO {
  categoria_id: string | null;
  subcategoria_id: string | null;
  despiece_producto_id: string | null;
  despiece_producto_nombre: string | null;
  despiece_factor: string | null;
  /** Solo roles autorizados (superadmin, admin_sucursal, contador); null para el resto. */
  costo_real: string | null;
  stock_sucursal: StockAlmacenDTO[];
  /** Referencia sin detalle (spec 02 regla 6). */
  otras_sucursales: { sucursal_id: string; sucursal_nombre: string; disponible: string }[];
  minimos: { sucursal_id: string; sucursal_nombre: string; minimo: string }[];
  creado_en: string;
  actualizado_en: string;
}

export interface HistoricoPrecioDTO {
  id: number;
  ocurrido_en: string;
  presentacion_nombre: string;
  lista_codigo: string;
  precio_anterior: string | null;
  precio_nuevo: string;
  usuario_nombre: string | null;
}
